import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useUserRole } from '../../contexts/UserRoleContext';
import { supabaseClient } from '../../services/supabase';
import { sendLocalGovAdminRequestEmail, sendLocalGovAdminRequestReceiptEmail } from '../../services/emailService';
import { supabaseLocationService } from '../../services/supabaseLocationService';
import Autocomplete from './Autocomplete';
import './UserProfile.css';

/** Roles that require Super Admin approval; users with role "user" can request these */
const REQUESTABLE_ROLES = ['developer', 'local_govt_admin'] as const;

interface UserProfileProps {
  isOpen: boolean;
  onClose: () => void;
}

// US States data
const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware',
  'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky',
  'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi',
  'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey', 'New Mexico',
  'New York', 'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania',
  'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont',
  'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming'
];

// Countries data (major countries)
const COUNTRIES = [
  'Afghanistan', 'Albania', 'Algeria', 'Argentina', 'Armenia', 'Australia', 'Austria', 'Azerbaijan',
  'Bangladesh', 'Belarus', 'Belgium', 'Brazil', 'Bulgaria', 'Cambodia', 'Canada', 'Chile', 'China',
  'Colombia', 'Croatia', 'Czech Republic', 'Denmark', 'Egypt', 'Estonia', 'Finland', 'France',
  'Georgia', 'Germany', 'Ghana', 'Greece', 'Hungary', 'Iceland', 'India', 'Indonesia', 'Iran',
  'Iraq', 'Ireland', 'Israel', 'Italy', 'Japan', 'Jordan', 'Kazakhstan', 'Kenya', 'Kuwait',
  'Latvia', 'Lebanon', 'Lithuania', 'Luxembourg', 'Malaysia', 'Mexico', 'Morocco', 'Netherlands',
  'New Zealand', 'Nigeria', 'Norway', 'Pakistan', 'Peru', 'Philippines', 'Poland', 'Portugal',
  'Qatar', 'Romania', 'Russia', 'Saudi Arabia', 'Singapore', 'Slovakia', 'Slovenia', 'South Africa',
  'South Korea', 'Spain', 'Sri Lanka', 'Sweden', 'Switzerland', 'Thailand', 'Turkey', 'Ukraine',
  'United Arab Emirates', 'United Kingdom', 'United States', 'Uruguay', 'Venezuela', 'Vietnam'
];

// Use case options
const USE_CASE_OPTIONS = [
  'Curiosity / general exploration',
  'Personal decisions (EVs, solar, etc.)',
  'Community advocacy / education',
  'Climate Action Plan development (local gov)',
  'Planning / infrastructure decisions (local gov)',
  'Published reports (gov/consultant/academic)',
  'Grant / funding applications',
  'State / regional program evaluation',
  'Academic research / teaching',
  'Media / journalism',
  'Nonprofit / NGO use',
  'Business / corporate sustainability',
  'Other'
];

const UserProfile: React.FC<UserProfileProps> = ({ isOpen, onClose }) => {
  const { user, signOut, updateProfile } = useAuth();
  const { userProfile, hasAnyRole, refreshProfile } = useUserRole();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  const [firstName, setFirstName] = useState(user?.user_metadata?.first_name || '');
  const [lastName, setLastName] = useState(user?.user_metadata?.last_name || '');
  const [organization, setOrganization] = useState(user?.user_metadata?.organization || '');
  const [userType, setUserType] = useState(user?.user_metadata?.user_type || '');
  const [locationType, setLocationType] = useState(user?.user_metadata?.location_type || '');
  const [specificLocation, setSpecificLocation] = useState(user?.user_metadata?.specific_location || '');
  const [useCases, setUseCases] = useState<string[]>(user?.user_metadata?.use_cases || []);
  const [otherUseCase, setOtherUseCase] = useState(user?.user_metadata?.other_use_case || '');
  const [acceptTerms, setAcceptTerms] = useState(user?.user_metadata?.accept_terms || false);
  const [acceptCommunications, setAcceptCommunications] = useState(user?.user_metadata?.accept_communications !== false);
  
  const [californiaLocations, setCaliforniaLocations] = useState<string[]>([]);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [userComments, setUserComments] = useState<any[]>([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [requestingRole, setRequestingRole] = useState<string | null>(null);
  const [pendingRequests, setPendingRequests] = useState<{ role: string }[]>([]);

  // Load California locations when component mounts
  useEffect(() => {
    const loadCaliforniaLocations = async () => {
      if (locationType === 'California') {
        setLoadingLocations(true);
        try {
          await supabaseLocationService.initialize();
          const locations = supabaseLocationService.getAvailableLocations();
          const locationNames = locations.map(loc => loc.name).sort();
          setCaliforniaLocations(locationNames);
        } catch (error) {
          console.error('Error loading California locations:', error);
        } finally {
          setLoadingLocations(false);
        }
      }
    };

    loadCaliforniaLocations();
  }, [locationType]);

  // Load user comments when component mounts
  useEffect(() => {
    const loadUserComments = async () => {
      if (!user) return;
      
      setLoadingComments(true);
      try {
        const { data, error } = await supabaseClient
          .from('community_comments')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false });

        if (error) {
          console.error('Error loading user comments:', error);
        } else {
          setUserComments(data || []);
        }
      } catch (error) {
        console.error('Error loading user comments:', error);
      } finally {
        setLoadingComments(false);
      }
    };

    loadUserComments();
  }, [user]);

  // Load pending role requests (users can select their own requests)
  useEffect(() => {
    const loadPendingRequests = async () => {
      if (!user) return;
      const { data, error } = await supabaseClient
        .from('admin_role_requests')
        .select('requested_role')
        .eq('user_id', user.id)
        .eq('status', 'pending');
      if (!error && data) {
        setPendingRequests(data.map((r) => ({ role: r.requested_role })));
      }
    };
    loadPendingRequests();
  }, [user]);

  // Reset specific location when location type changes
  useEffect(() => {
    setSpecificLocation('');
  }, [locationType]);

  // Handle use case checkbox changes
  const handleUseCaseChange = (useCase: string, checked: boolean) => {
    if (checked) {
      setUseCases(prev => [...prev, useCase]);
    } else {
      setUseCases(prev => prev.filter(uc => uc !== useCase));
    }
  };

  // Sanitize and validate other use case input
  const handleOtherUseCaseChange = (value: string) => {
    // Remove excessive whitespace and limit repeated characters
    const sanitized = value
      .trim()
      .replace(/\s+/g, ' ') // Replace multiple spaces with single space
      .replace(/(.)\1{4,}/g, '$1$1$1$1') // Limit repeated characters to max 4
      .replace(/[<>]/g, '') // Remove potential HTML tags
      .substring(0, 100); // Enforce max length
    
    setOtherUseCase(sanitized);
  };

  /** Request an elevated role (Developer or Local Government Admin). Super Admins are notified and must approve. */
  const handleRequestRole = async (requestedRole: typeof REQUESTABLE_ROLES[number]) => {
    if (!user) return;
    setRequestingRole(requestedRole);
    setError(null);
    setSuccess(null);
    try {
      const requesterName = [firstName, lastName].filter(Boolean).join(' ') || user.email?.split('@')[0] || 'User';
      const requesterEmail = user.email || '';
      const requestedLocation = requestedRole === 'local_govt_admin' ? specificLocation || '' : 'N/A';
      const requestedLocationType = requestedRole === 'local_govt_admin' ? locationType || '' : 'N/A';

      const { data: requestRow, error: requestError } = await supabaseClient
        .from('admin_role_requests')
        .insert({
          user_id: user.id,
          requester_email: requesterEmail,
          requester_name: requesterName,
          requested_role: requestedRole,
          requested_location: requestedLocation,
          requested_location_type: requestedLocationType,
          status: 'pending'
        })
        .select('id')
        .single();

      if (requestError) {
        setError(requestError.message || 'Could not submit role request.');
        return;
      }

      await supabaseClient
        .from('admin_notifications')
        .insert({
          type: 'role_request',
          title: requestedRole === 'developer' ? 'Developer Access Request' : 'Local Government Admin Request',
          message: requestedRole === 'developer'
            ? `${requesterName} (${requesterEmail}) requested Developer access.`
            : `${requesterName} requested Local Government Administrator access for ${requestedLocation}${requestedLocationType ? ` (${requestedLocationType})` : ''}.`,
          metadata: {
            request_id: requestRow?.id,
            requester_name: requesterName,
            requester_email: requesterEmail,
            requested_role: requestedRole,
            requester_location: requestedLocation,
            requester_location_type: requestedLocationType
          },
          created_by: user.id,
          target_role: 'super_admin'
        });

      await supabaseClient
        .from('user_notifications')
        .insert({
          user_id: user.id,
          type: 'role_request',
          title: 'Request received',
          message: requestedRole === 'developer'
            ? 'Your request for Developer access has been received. A Super Admin will review it.'
            : `Your request to become a Local Government Administrator for ${requestedLocation} has been received and is under review.`,
          metadata: { requested_role: requestedRole }
        });

      if (requestedRole === 'local_govt_admin') {
        await supabaseClient
          .from('user_profiles')
          .upsert({
            id: user.id,
            email: requesterEmail,
            full_name: requesterName,
            role: 'local_govt_admin_pending',
            updated_at: new Date().toISOString()
          }, { onConflict: 'id' });
        await refreshProfile();
        await sendLocalGovAdminRequestEmail({
          requesterName,
          requesterEmail,
          requesterLocation: requestedLocation,
          requesterLocationType: requestedLocationType
        });
        await sendLocalGovAdminRequestReceiptEmail({
          requesterName,
          requesterEmail,
          requesterLocation: requestedLocation,
          requesterLocationType: requestedLocationType
        });
      }

      setPendingRequests((prev) => (prev.some((r) => r.role === requestedRole) ? prev : [...prev, { role: requestedRole }]));
      setSuccess(requestedRole === 'developer' ? 'Your Developer access request has been submitted. Super Admins will be notified and will review it.' : 'Your Local Government Administrator request has been submitted. Super Admins will be notified and will review it.');
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new Event('notifications:refresh'));
      }
    } catch (err) {
      console.error('Error requesting role:', err);
      setError('Something went wrong. Please try again.');
    } finally {
      setRequestingRole(null);
    }
  };

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    if (!user) {
      setError('You must be signed in to update your profile');
      setLoading(false);
      return;
    }

    // Validate required fields
    if (!firstName.trim() || !lastName.trim() || !organization.trim() || !userType || !locationType || !specificLocation || useCases.length === 0) {
      setError('Please fill in all required fields to complete your profile');
      setLoading(false);
      return;
    }

    // Validate terms acceptance
    if (!acceptTerms) {
      setError('You must accept the Terms of Use and Privacy Statement to complete your profile');
      setLoading(false);
      return;
    }

    // Additional validation for "Other" use case
    if (useCases.includes('Other') && (!otherUseCase || otherUseCase.trim().length < 3)) {
      setError('Please provide a description for "Other" use case (minimum 3 characters)');
      setLoading(false);
      return;
    }

    // Check for potential spam patterns
    if (otherUseCase && (
      otherUseCase.length > 50 && 
      (otherUseCase.match(/\b(click|buy|sell|free|money|cash|win|prize|offer|deal|discount|promo|spam|advertisement|advertise)\b/gi) || 
       otherUseCase.match(/[!@#$%^&*()_+={}[\]|\\:";'<>?,./]{3,}/) ||
       otherUseCase.match(/\b(www\.|http|\.com|\.org|\.net)\b/gi))
    )) {
      setError('Please provide a valid use case description');
      setLoading(false);
      return;
    }

    const { error } = await updateProfile({
      first_name: firstName,
      last_name: lastName,
      organization: organization,
      user_type: userType,
      location_type: locationType,
      specific_location: specificLocation,
      use_cases: useCases,
      other_use_case: otherUseCase,
      accept_terms: acceptTerms,
      accept_communications: acceptCommunications
    });

    if (error) {
      setError(error.message);
    } else {
      if (userType === 'Local Government Administrator*') {
        try {
          const requesterName = `${firstName} ${lastName}`.trim();
          const requesterLocation = specificLocation;
          const requesterEmail = user.email || '';
          const { data: existingRequest, error: existingError } = await supabaseClient
            .from('admin_role_requests')
            .select('id, status')
            .eq('user_id', user.id)
            .eq('requested_role', 'local_govt_admin')
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle();

          if (existingError) {
            console.error('Error checking existing role request:', existingError);
          }

          if (!existingRequest || existingRequest.status !== 'pending') {
            const { data: requestRow, error: requestError } = await supabaseClient
              .from('admin_role_requests')
              .insert({
                user_id: user.id,
                requester_email: requesterEmail,
                requester_name: requesterName,
                requested_role: 'local_govt_admin',
                requested_location: requesterLocation,
                requested_location_type: locationType,
                status: 'pending'
              })
              .select('id')
              .single();

            if (requestError) {
              console.error('Error creating role request:', requestError);
            } else {
              await supabaseClient
                .from('admin_notifications')
                .insert({
                  type: 'role_request',
                  title: 'Local Government Admin Request',
                  message: `${requesterName} requested Local Government Administrator access for ${requesterLocation}${locationType ? ` (${locationType})` : ''}.`,
                  metadata: {
                    request_id: requestRow?.id,
                    requester_name: requesterName,
                    requester_email: requesterEmail,
                    requester_location: requesterLocation,
                    requester_location_type: locationType
                  },
                  created_by: user.id,
                  target_role: 'super_admin'
                });
            }
          }

          const { data: existingUserNotice, error: existingNoticeError } = await supabaseClient
            .from('user_notifications')
            .select('id, created_at')
            .eq('user_id', user.id)
            .eq('type', 'role_request')
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle();

          if (existingNoticeError) {
            console.error('Error checking existing user notifications:', existingNoticeError);
          }

          if (!existingUserNotice) {
            await supabaseClient
              .from('user_notifications')
              .insert({
                user_id: user.id,
                type: 'role_request',
                title: 'Request received',
                message: `CoolClimate Network has received your request to become a Local Government Administrator for ${requesterLocation}. Your request is under review.`,
                metadata: {
                  requested_role: 'local_govt_admin',
                  requester_location: requesterLocation,
                  requester_location_type: locationType
                }
              });

            await sendLocalGovAdminRequestReceiptEmail({
              requesterName,
              requesterEmail: requesterEmail,
              requesterLocation: requesterLocation,
              requesterLocationType: locationType
            });

            if (typeof window !== 'undefined') {
              window.dispatchEvent(new Event('notifications:refresh'));
            }
          }

          await supabaseClient
            .from('user_profiles')
            .upsert({
              id: user.id,
              email: requesterEmail,
              full_name: requesterName,
              role: 'local_govt_admin_pending',
              updated_at: new Date().toISOString()
            }, { onConflict: 'id' });

          await sendLocalGovAdminRequestEmail({
            requesterName,
            requesterEmail: requesterEmail,
            requesterLocation: requesterLocation,
            requesterLocationType: locationType
          });
        } catch (requestErr) {
          console.error('Error requesting local government admin role:', requestErr);
        }

        setSuccess('Profile updated. Your Local Government Administrator request is under review. A confirmation email has been sent.');
      } else if (userType === 'Academic') {
        try {
          const requesterName = `${firstName} ${lastName}`.trim();
          const requesterLocation = specificLocation;
          const requesterEmail = user.email || '';
          const { data: existingRequest, error: existingError } = await supabaseClient
            .from('admin_role_requests')
            .select('id, status')
            .eq('user_id', user.id)
            .eq('requested_role', 'academic')
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle();

          if (existingError) {
            console.error('Error checking existing academic role request:', existingError);
          }

          if (!existingRequest || existingRequest.status !== 'pending') {
            const { data: requestRow, error: requestError } = await supabaseClient
              .from('admin_role_requests')
              .insert({
                user_id: user.id,
                requester_email: requesterEmail,
                requester_name: requesterName,
                requested_role: 'academic',
                requested_location: requesterLocation || 'N/A',
                requested_location_type: locationType || 'N/A',
                status: 'pending'
              })
              .select('id')
              .single();

            if (requestError) {
              console.error('Error creating academic role request:', requestError);
            } else {
              await supabaseClient
                .from('admin_notifications')
                .insert({
                  type: 'role_request',
                  title: 'Academic Role Request',
                  message: `${requesterName} (${requesterEmail}) requested Academic access.`,
                  metadata: {
                    request_id: requestRow?.id,
                    requester_name: requesterName,
                    requester_email: requesterEmail,
                    requested_role: 'academic',
                    requester_location: requesterLocation || 'N/A',
                    requester_location_type: locationType || 'N/A'
                  },
                  created_by: user.id,
                  target_role: 'super_admin'
                });
            }
          }

          const { data: existingUserNotice, error: existingNoticeError } = await supabaseClient
            .from('user_notifications')
            .select('id')
            .eq('user_id', user.id)
            .eq('type', 'role_request')
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle();

          if (existingNoticeError) {
            console.error('Error checking existing academic request notification:', existingNoticeError);
          }

          if (!existingUserNotice) {
            await supabaseClient
              .from('user_notifications')
              .insert({
                user_id: user.id,
                type: 'role_request',
                title: 'Request received',
                message: 'Your Academic role request has been received and is under review by a Super Admin.',
                metadata: {
                  requested_role: 'academic',
                  requester_location: requesterLocation || 'N/A',
                  requester_location_type: locationType || 'N/A'
                }
              });
          }

          if (typeof window !== 'undefined') {
            window.dispatchEvent(new Event('notifications:refresh'));
          }
        } catch (requestErr) {
          console.error('Error requesting academic role:', requestErr);
        }

        setSuccess('Profile updated. Your Academic role request is under review. Super Admins have been notified.');
      } else {
        setSuccess('Profile updated successfully!');
      }
    }

    setLoading(false);
  };

  const handleSignOut = async () => {
    setLoading(true);
    const { error } = await signOut();
    if (error) {
      setError(error.message);
    } else {
      onClose();
    }
    setLoading(false);
  };

  if (!isOpen || !user) return null;

  return (
    <div className="user-profile-overlay" onClick={onClose}>
      <div className="user-profile-modal" onClick={(e) => e.stopPropagation()}>
        <div className="user-profile-header">
          <h2>Profile</h2>
          <button className="user-profile-close" onClick={onClose}>×</button>
        </div>

        <div className="user-profile-body">
          <div className="user-profile-message">
            <h3 className="user-profile-message-title">Why Profile is Required for Data Download</h3>
            <p>Help keep ClimatePlans.org free for everyone. CoolClimate and potential funders need to know how ClimatePlans.org contributes to equitable climate action planning throughout California. Thank you! ~CoolClimate Network</p>
          </div>

          {error && (
            <div className="user-profile-error">
              {error}
            </div>
          )}

          {success && (
            <div className="user-profile-success">
              {success}
            </div>
          )}

          {/* Request elevated role: only for users who are not already developer/super_admin/local_govt_admin */}
          {userProfile && !hasAnyRole(['super_admin', 'developer', 'local_govt_admin']) && (
            <div className="user-profile-role-requests">
              <h4>Request elevated access</h4>
              <p className="user-profile-role-requests-note">
                Developer, Local Government Administrator, and Academic access require Super Admin approval. Super Admins will be notified when you submit a request.
              </p>
              <div className="user-profile-role-requests-buttons">
                <div className="user-profile-role-request-row">
                  <span className="user-profile-role-label">Developer:</span>
                  {pendingRequests.some((r) => r.role === 'developer') ? (
                    <span className="user-profile-role-pending">Request pending</span>
                  ) : (
                    <button
                      type="button"
                      className="user-profile-button user-profile-button-secondary"
                      disabled={!!requestingRole}
                      onClick={() => handleRequestRole('developer')}
                    >
                      {requestingRole === 'developer' ? 'Submitting…' : 'Request Developer access'}
                    </button>
                  )}
                </div>
                <p className="user-profile-role-hint">
                  To request <strong>Local Government Administrator</strong> access, select &quot;Local Government Administrator*&quot; below, enter your location, and click Update Profile.
                </p>
              </div>
            </div>
          )}

          <div className="user-profile-info">
              <div className="user-profile-avatar">
                {user.user_metadata?.first_name?.[0] || user.email?.[0] || 'U'}
              </div>
            <div className="user-profile-details">
              <h3>{user.user_metadata?.first_name} {user.user_metadata?.last_name}</h3>
              <p>{user.email}</p>
              <p className="user-profile-org">{user.user_metadata?.organization || 'No organization'}</p>
              {user.user_metadata?.user_type && (
                <p className="user-profile-type">Type: {user.user_metadata.user_type}</p>
              )}
              {user.user_metadata?.location_type && user.user_metadata?.specific_location && (
                <p className="user-profile-location">
                  Location: {user.user_metadata.specific_location}, {user.user_metadata.location_type}
                </p>
              )}
              {((user.user_metadata?.use_cases && user.user_metadata.use_cases.length > 0) || user.user_metadata?.other_use_case) && (
                <div className="user-profile-use-cases">
                  <p className="user-profile-use-cases-title">Use Cases:</p>
                  <div className="user-profile-use-cases-list">
                    {user.user_metadata?.use_cases && user.user_metadata.use_cases.map((useCase: string, index: number) => (
                      <span key={index} className="user-profile-use-case-tag">
                        {useCase}
                      </span>
                    ))}
                    {user.user_metadata?.other_use_case && (
                      <span className="user-profile-use-case-tag user-profile-use-case-other">
                        Other: {user.user_metadata.other_use_case}
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          <form onSubmit={handleUpdateProfile} className="user-profile-form">
            <h4>Update Profile</h4>
            
            <div className="user-profile-form-row">
              <div className="user-profile-form-group">
                <label htmlFor="firstName">First Name *</label>
                <input
                  id="firstName"
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  disabled={loading}
                  placeholder="First name"
                  required
                />
              </div>

              <div className="user-profile-form-group">
                <label htmlFor="lastName">Last Name *</label>
                <input
                  id="lastName"
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  disabled={loading}
                  placeholder="Last name"
                  required
                />
              </div>
            </div>

            <div className="user-profile-form-group">
              <label htmlFor="organization">Organization (write "none" if not applicable) *</label>
              <input
                id="organization"
                type="text"
                value={organization}
                onChange={(e) => setOrganization(e.target.value)}
                disabled={loading}
                placeholder="Write 'none' if not applicable"
                required
              />
            </div>

            <div className="user-profile-form-group">
              <label htmlFor="userType">User Type *</label>
              <select
                id="userType"
                value={userType}
                onChange={(e) => setUserType(e.target.value)}
                disabled={loading}
                required
              >
                <option value="">Select user type</option>
                <option value="Resident">Resident</option>
                <option value="Local Government">Local Government</option>
                <option value="Local Government Administrator*">Local Government Administrator*</option>
                <option value="State Government">State Government</option>
                <option value="Other Government Organization">Other Government Organization</option>
                <option value="Business">Business</option>
                <option value="Academic">Academic</option>
                <option value="Non-Profit">Non-Profit</option>
                <option value="Consultancy">Consultancy</option>
                <option value="Other">Other</option>
              </select>
              {userType === 'Local Government Administrator*' && (
                <p className="user-profile-note">
                  * Requires approval. We will notify administrators with your name and location.
                </p>
              )}
              {userType === 'Academic' && (
                <p className="user-profile-note">
                  Academic role requires Super Admin approval. Submitting this profile will create a pending request.
                </p>
              )}
            </div>

            <div className="user-profile-form-group">
              <label htmlFor="locationType">My Location *</label>
              <select
                id="locationType"
                value={locationType}
                onChange={(e) => setLocationType(e.target.value)}
                disabled={loading}
                required
              >
                <option value="">Select location type</option>
                <option value="California">California</option>
                <option value="Other US State">Other US State</option>
                <option value="Other Country">Other Country</option>
              </select>
            </div>

            {locationType && (
              <div className="user-profile-form-group">
                <label htmlFor="specificLocation">
                  {locationType === 'California' ? 'California Location' :
                   locationType === 'Other US State' ? 'US State' : 'Country'} *
                </label>
                <Autocomplete
                  id="specificLocation"
                  options={
                    locationType === 'California' ? californiaLocations :
                    locationType === 'Other US State' ? US_STATES.filter(state => state !== 'California') :
                    COUNTRIES.filter(country => country !== 'United States')
                  }
                  value={specificLocation}
                  onChange={setSpecificLocation}
                  placeholder={`Type to search ${locationType === 'California' ? 'locations' : locationType === 'Other US State' ? 'states' : 'countries'}...`}
                  disabled={loading}
                  loading={loadingLocations}
                />
                {loadingLocations && (
                  <div className="user-profile-loading">Loading locations...</div>
                )}
              </div>
            )}

            <div className="user-profile-form-group">
              <label>Use Case *</label>
              <div className="user-profile-checkbox-group">
                {USE_CASE_OPTIONS.map((useCase) => (
                  <label key={useCase} className="user-profile-checkbox-item">
                    <input
                      type="checkbox"
                      checked={useCases.includes(useCase)}
                      onChange={(e) => handleUseCaseChange(useCase, e.target.checked)}
                      disabled={loading}
                    />
                    <span className="user-profile-checkbox-label">{useCase}</span>
                  </label>
                ))}
              </div>
              {useCases.includes('Other') && (
                <div className="user-profile-other-input">
                  <input
                    type="text"
                    value={otherUseCase}
                    onChange={(e) => handleOtherUseCaseChange(e.target.value)}
                    disabled={loading}
                    placeholder="Please specify your use case"
                    className="user-profile-other-text-input"
                    maxLength={100}
                  />
                  <div className={`user-profile-char-count ${
                    otherUseCase.length > 90 ? 'error' : 
                    otherUseCase.length > 80 ? 'warning' : ''
                  }`}>
                    {otherUseCase.length}/100 characters
                  </div>
                </div>
              )}
              <div className="user-profile-checkbox-help">
                Please click all that apply
              </div>
            </div>

            <div className="user-profile-form-group">
              <label className="user-profile-checkbox-label">
                <input
                  type="checkbox"
                  checked={acceptTerms}
                  onChange={(e) => setAcceptTerms(e.target.checked)}
                  disabled={loading}
                  required
                />
                <span className="user-profile-checkbox-text">
                  I accept the{' '}
                  <a 
                    href="https://coolclimate.berkeley.edu/files2/CoolClimate-terms-of-use.pdf" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="user-profile-link"
                  >
                    Terms of Use and Privacy Statement
                  </a>
                </span>
              </label>
            </div>

            <div className="user-profile-form-group">
              <label className="user-profile-checkbox-label">
                <input
                  type="checkbox"
                  checked={acceptCommunications}
                  onChange={(e) => setAcceptCommunications(e.target.checked)}
                  disabled={loading}
                />
                <span className="user-profile-checkbox-text">
                  I agree to receive communications from ClimatePlans. We will not share contact information with third parties.
                </span>
              </label>
            </div>

            <button
              type="submit"
              className="user-profile-button user-profile-button-primary"
              disabled={loading}
            >
              {loading ? 'Updating...' : 'Update Profile'}
            </button>
          </form>

          {/* User Comments Section */}
          <div className="user-profile-comments">
            <div className="user-profile-comments-header">
              <h4>My Comments ({userComments.length})</h4>
              <button
                className="user-profile-comments-toggle"
                onClick={() => setShowComments(!showComments)}
              >
                {showComments ? 'Hide' : 'Show'}
              </button>
            </div>

            {/* Link to full Community page (moved under profile) */}
            <div style={{ marginTop: '8px', marginBottom: '12px' }}>
              <a href="/community" className="user-profile-link" style={{ textDecoration: 'underline' }}>
                View Community discussions and all chart comments →
              </a>
            </div>
            
            {showComments && (
              <div className="user-profile-comments-list">
                {loadingComments ? (
                  <div className="user-profile-comments-loading">
                    Loading comments...
                  </div>
                ) : userComments.length === 0 ? (
                  <div className="user-profile-comments-empty">
                    No comments yet. Start participating in community discussions!
                  </div>
                ) : (
                  userComments.map(comment => (
                    <div key={comment.id} className="user-profile-comment">
                      <div className="user-profile-comment-header">
                        <span className="user-profile-comment-type">
                          {comment.post_type?.replace('_', ' ').toUpperCase() || 'COMMENT'}
                        </span>
                        <span className="user-profile-comment-date">
                          {new Date(comment.created_at).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </span>
                      </div>
                      <div className="user-profile-comment-content">
                        {comment.content}
                      </div>
                      {comment.chart_context && (
                        <div className="user-profile-comment-context">
                          <strong>Chart:</strong> {comment.chart_context}
                        </div>
                      )}
                      {comment.location_context && (
                        <div className="user-profile-comment-context">
                          <strong>Location:</strong> {comment.location_context}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}
          </div>

          <div className="user-profile-actions">
            <button
              className="user-profile-button user-profile-button-secondary"
              onClick={handleSignOut}
              disabled={loading}
            >
              Sign Out
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserProfile;
